import React, { useState } from 'react';
import { useInView } from '../../../hooks/useInView';

const ROADMAP = [
  {
    status: 'live' as const,
    title: 'Decision-maker identification',
    body: 'Find the exact person running any local business using address as the identifier.',
    bullets: [
      'Business name and address input',
      'Up to 5 leadership roles per search',
      'Name and job title returned',
      'CSV bulk upload and timestamped export',
      'Credit-based pricing, GDPR compliant',
    ],
  },
  {
    status: 'soon' as const,
    title: 'Email and phone enrichment',
    body: 'Every decision maker automatically enriched with a verified email and mobile number.',
    bullets: [
      'Work and personal emails, under 3% bounce rate',
      'Generic email as last resort, also catch-all verified',
      'Verified mobile only, no landlines',
      'Invalid results not charged',
    ],
  },
  {
    status: 'soon' as const,
    title: 'AI deep-search',
    body: "A second pass for contacts that standard searches can't surface.",
    bullets: [
      'Runs automatically when standard search returns no result',
      'No extra input required',
      'Higher hit rate on hard-to-find contacts',
    ],
  },
  {
    status: 'soon' as const,
    title: 'Local business signals',
    body: 'Buying signals built for local businesses, know when a shop is ready.',
    bullets: [
      'Ad activity detection across major platforms',
      'Ownership and job title change alerts',
      'Growth trigger identification',
    ],
  },
];

export default function UseCases() {
  const [hovered, setHovered] = useState<number | null>(null);
  const { ref, inView } = useInView(0.06);

  return (
    <>
      {/* ── Yellow marquee strip ── */}
      <div className="bg-brand-500 overflow-hidden py-5 border-y-4 border-slate-900">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(3)].map((_, rep) => (
            <div key={rep} className="flex items-center gap-0 flex-shrink-0">
              {['1 credit per company', 'Live search',  'GDPR clean', '30-day auto deletion', '3,000+ categories', 'No credit card required', 'Every contact included'].map((text, i) => (
                <React.Fragment key={i}>
                  <span className="text-[15px] font-black text-slate-900 uppercase tracking-widest px-8">{text}</span>
                  <span className="text-slate-900/30 text-[20px] font-black">·</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Roadmap section ── */}
      <section id="roadmap" ref={ref as React.RefObject<HTMLElement>} className="bg-slate-50 py-24 sm:py-36 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">

          {/* giant statement */}
          <div
            className="mb-20 overflow-hidden"
            style={{ opacity: inView ? 1 : 0, transition: 'opacity 0.7s ease' }}
          >
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-brand-700 mb-5">Roadmap</p>
            <div className="relative -mx-6 px-6 lg:-mx-8 lg:px-8">
              <h2 className="mb-5 text-[1.6rem] sm:text-[5.5rem] lg:text-[5.2rem] xl:text-[6rem] font-black tracking-[-0.045em] text-slate-900 leading-[0.9] whitespace-nowrap">
                Built for where
              </h2>
              <div className="flex items-baseline gap-4">
                <h2 className="text-[1.6rem] sm:text-[5.5rem] lg:text-[5.2rem] xl:text-[6rem] font-black tracking-[-0.045em] leading-[0.9] whitespace-nowrap text-slate-900">
                  local
                </h2>
                <h2 className="text-[1.6rem] sm:text-[5.5rem] lg:text-[5.2rem] xl:text-[6rem] font-black tracking-[-0.045em] leading-[0.9] whitespace-nowrap">
                  <span className="bg-brand-500 text-slate-900 px-2 md:px-4 rounded-xl sm:rounded-2xl">outreach</span>
                </h2>
                <h2 className="text-[1.6rem] sm:text-[5.5rem] lg:text-[5.2rem] xl:text-[6rem] font-black tracking-[-0.045em] leading-[0.9] whitespace-nowrap text-slate-900">
                  is going.
                </h2>
              </div>
            </div>
            <p className="text-[15px] text-slate-400 max-w-md mt-6 leading-relaxed">
              A complete data and intelligence layer for every local business you will ever want to reach.
            </p>
          </div>

          {/* roadmap cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ROADMAP.map((card, i) => {
              const isHov = hovered === i;
              const isLive = card.status === 'live';
              return (
                <div
                  key={i}
                  className={`rounded-3xl p-8 border-2 transition-all duration-300 cursor-default ${
                    isLive
                      ? isHov
                        ? 'bg-brand-500 border-brand-500 -translate-y-2 shadow-2xl shadow-brand-500/25'
                        : 'bg-white border-brand-500/30 hover:-translate-y-1 shadow-lg shadow-brand-500/10'
                      : isHov
                        ? 'bg-slate-900 border-slate-900 -translate-y-2 shadow-xl'
                        : 'bg-white border-slate-200 hover:border-slate-300 hover:-translate-y-1'
                  }`}
                  style={{
                    opacity: inView ? 1 : 0,
                    transition: `opacity 0.5s ease ${i * 100}ms, transform 0.3s ease, box-shadow 0.3s ease`,
                  }}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div className="flex items-center gap-3 mb-5">
                    {isLive ? (
                      <span className={`flex items-center gap-2 text-[11px] font-black uppercase tracking-widest rounded-full px-3 py-1 border ${
                        isHov
                          ? 'text-slate-900 bg-slate-900/15 border-slate-900/25'
                          : 'text-brand-700 bg-brand-500/10 border-brand-500/25'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${isHov ? 'bg-slate-900' : 'bg-brand-500'}`} />
                        Live now
                      </span>
                    ) : (
                      <span className={`text-[11px] font-black uppercase tracking-widest rounded-full px-3 py-1 border transition-all duration-300 ${
                        isHov
                          ? 'text-slate-400 bg-slate-800 border-slate-700'
                          : 'text-slate-400 bg-slate-100 border-slate-200'
                      }`}>
                        Coming soon
                      </span>
                    )}
                  </div>
                  <h3 className={`text-[20px] sm:text-[22px] font-black leading-snug mb-3 transition-colors duration-300 ${
                    isLive && isHov ? 'text-slate-900' : isHov ? 'text-white' : 'text-slate-900'
                  }`}>{card.title}</h3>
                  <p className={`text-[14px] leading-relaxed mb-6 transition-colors duration-300 ${
                    isLive && isHov ? 'text-slate-800' : isHov ? 'text-slate-400' : 'text-slate-500'
                  }`}>{card.body}</p>
                  <ul className="space-y-2.5">
                    {card.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-300 ${
                          isLive && isHov
                            ? 'border-slate-900 bg-slate-900/15'
                            : isHov
                              ? 'border-brand-500 bg-brand-500/10'
                              : isLive
                                ? 'border-brand-500/50'
                                : 'border-slate-300'
                        }`}>
                          <span className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                            isLive && isHov ? 'bg-slate-900' : isHov ? 'bg-brand-500' : isLive ? 'bg-brand-500/50' : 'bg-slate-300'
                          }`} />
                        </div>
                        <span className={`text-[13.5px] transition-colors duration-300 ${
                          isLive && isHov ? 'text-slate-800' : isHov ? 'text-slate-300' : 'text-slate-500'
                        }`}>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .animate-marquee { animation: marquee 20s linear infinite; }
      `}</style>
    </>
  );
}
